import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { readMediaObjectUrl } from '@/lib/api/client/inventory'
import type { ProductMediaItem } from '@/lib/api/schemas/inventory.schema'
import { productMediaKindLabels } from '@/lib/warehouse/labels'

/**
 * The media payload every surface reads, derived from `ProductMediaItemSchema`
 * so it cannot drift from the server contract. Defined over the whole row, not
 * over what this component renders: the feed needs `status` and `failureReason`
 * for rows the lightbox never shows in that state, and a narrowed type would
 * force it to declare a second one.
 */
export type MediaItem = ProductMediaItem

/**
 * Where the stage gets its bytes. A catalog picture is a provider CDN URL that
 * belongs in `src`; a `product_media` row is served by the Clerk-protected byte
 * route, where a `src` answers 401 and renders broken with nothing to report.
 * Which route applies is stated, never inferred from which field is empty.
 */
export type MediaLightboxEntry =
  | { readonly origin: 'catalog'; readonly url: string; readonly alt: string }
  | { readonly origin: 'media'; readonly item: MediaItem }

export interface MediaLightboxProps {
  entries: readonly MediaLightboxEntry[]
  initialIndex?: number
  /** Article and title, shown in the top bar of the warehouse card. */
  heading?: string
  onClose: () => void
  onMakePrimary?: (item: MediaItem) => void
  onDelete?: (item: MediaItem) => void
}

const LOADING_TEXT = 'Загружаем файл…'
const TRANSPORT_FAILURE_TEXT = 'Не удалось загрузить файл.'
const PRIMARY_CAPTION_LEAD = 'Главное фото'
const POSITION_CAPTION_LEAD = 'позиция'
const MAKE_PRIMARY_TEXT = 'Сделать главным'
const MAKE_PRIMARY_PHOTO_ONLY_TEXT = 'Только для фото'
const ALREADY_PRIMARY_TEXT = 'Уже главное'
const DELETE_TEXT = 'Удалить'
const CLOSE_TEXT = 'Закрыть фото'
const DISMISS_HINT_TEXT = 'Нажмите вне изображения или Escape, чтобы закрыть'
const PREVIOUS_TEXT = 'Предыдущий файл'
const NEXT_TEXT = 'Следующий файл'

// Not square: a piece is photographed portrait as often as landscape, and a
// square stage spends half its height on nothing while shrinking the picture.
// The box takes what the viewport can give and the picture is contained inside
// it, so a long pair of earrings is never cut off at the bottom.
const STAGE_CLASS = 'grid size-[min(900px,calc(100vw-8rem),calc(100dvh-16rem))] place-items-center overflow-hidden rounded-md bg-onyx-2'
// `contain` deliberately, unlike the grid tile's `cover`: this is where the
// operator inspects the true proportion of the piece.
// The cap is the stage's own side in absolute terms rather than a percentage:
// a percentage max-height on a centred grid item does not always resolve, and
// the picture was reaching past the bottom of the square because of it.
const STAGE_MEDIA_CLASS = 'max-h-[min(900px,calc(100vw-8rem),calc(100dvh-16rem))] max-w-[min(900px,calc(100vw-8rem),calc(100dvh-16rem))] object-contain'
const THUMB_CLASS = 'size-full object-cover'

function clampIndex(index: number, total: number): number {
  return Math.min(Math.max(index, 0), Math.max(total - 1, 0))
}

/**
 * One item's bytes, fetched with the session token and handed to the DOM as a
 * blob URL this hook revokes — on unmount and whenever the item changes.
 */
type CachedMediaObject = {
  promise: Promise<string>
  objectUrl: string | null
  references: number
}

const mediaObjectCache = new Map<string, CachedMediaObject>()

/** Share one authenticated byte fetch between a tile, stage and filmstrip. */
export function useMediaObjectUrl(mediaUrl: string | null): { objectUrl: string | null; failure: string | null } {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    if (mediaUrl === null) return undefined
    setObjectUrl(null)
    setFailure(null)
    let cached = mediaObjectCache.get(mediaUrl)
    if (cached === undefined) {
      cached = {
        promise: readMediaObjectUrl(mediaUrl),
        objectUrl: null,
        references: 0,
      }
      mediaObjectCache.set(mediaUrl, cached)
    }
    const cacheEntry = cached
    cacheEntry.references += 1
    let active = true
    cacheEntry.promise
      .then((next) => {
        cacheEntry.objectUrl = next
        if (active) setObjectUrl(next)
        if (cacheEntry.references === 0) {
          URL.revokeObjectURL(next)
          mediaObjectCache.delete(mediaUrl)
        }
      })
      .catch((reason: unknown) => {
        mediaObjectCache.delete(mediaUrl)
        if (!active) return
        // `MediaBytesUnavailableError` carries the server's own Russian sentence;
        // only a transport failure with no body falls back to one we own.
        setFailure(reason instanceof Error ? reason.message : TRANSPORT_FAILURE_TEXT)
      })
    return () => {
      active = false
      cacheEntry.references -= 1
      if (cacheEntry.references === 0 && cacheEntry.objectUrl !== null) {
        URL.revokeObjectURL(cacheEntry.objectUrl)
        mediaObjectCache.delete(mediaUrl)
      }
    }
  }, [mediaUrl])

  return { objectUrl, failure }
}

function mediaLabel(item: MediaItem): string {
  return `${productMediaKindLabels[item.kind]} ${item.position}`
}

function entryLabel(entry: MediaLightboxEntry): string {
  return entry.origin === 'catalog' ? entry.alt : mediaLabel(entry.item)
}

function positionCaption(item: MediaItem, merchandisingPosition: number | null): string {
  if (item.kind === 'instruction' || item.kind === 'unknown') {
    return productMediaKindLabels[item.kind]
  }
  const lead = item.isPrimary ? PRIMARY_CAPTION_LEAD : productMediaKindLabels[item.kind]
  return `${lead} · ${POSITION_CAPTION_LEAD} ${merchandisingPosition ?? item.position}`
}

function MediaStage({ item }: { item: MediaItem }) {
  const { objectUrl, failure } = useMediaObjectUrl(item.mediaUrl)

  if (item.mediaUrl === null) {
    return (
      <div className={STAGE_CLASS} data-reason={item.unavailableReason ?? undefined} data-position={item.position}>
        <div className="px-6 text-center">
          <p className="text-[13px] text-ivory/90">{item.unavailableMessage}</p>
        </div>
      </div>
    )
  }
  if (failure) {
    return (
      <div className={STAGE_CLASS} data-position={item.position}>
        <p role="alert" className="px-6 text-center text-[13px] text-st-unpaid">{failure}</p>
      </div>
    )
  }
  if (objectUrl === null) {
    return (
      <div className={STAGE_CLASS} data-position={item.position}>
        <p aria-live="polite" className="text-[13px] text-ivory/70">{LOADING_TEXT}</p>
      </div>
    )
  }
  return (
    <div className={STAGE_CLASS} data-position={item.position}>
      {item.kind === 'video' ? (
        <video controls preload="metadata" src={objectUrl} aria-label={mediaLabel(item)} className={STAGE_MEDIA_CLASS} />
      ) : (
        <img src={objectUrl} alt={mediaLabel(item)} className={STAGE_MEDIA_CLASS} />
      )}
    </div>
  )
}

function FilmstripThumb({ item }: { item: MediaItem }) {
  const { objectUrl } = useMediaObjectUrl(item.mediaUrl)
  if (objectUrl === null) return <span aria-hidden="true" className="size-full bg-onyx-3" />
  if (item.kind === 'video') return <video muted preload="metadata" src={objectUrl} aria-hidden="true" className={THUMB_CLASS} />
  return <img src={objectUrl} alt="" className={THUMB_CLASS} />
}

/**
 * The one enlarged-media surface in the tree.
 *
 * Escape, a backdrop click and the arrow keys are handled here so no call site
 * repeats them, and focus returns to whatever opened it.
 */
export function MediaLightbox({ entries, initialIndex = 0, heading, onClose, onMakePrimary, onDelete }: MediaLightboxProps) {
  const total = entries.length
  // Keep the opening selection as a prop-derived value until the operator
  // navigates. This also handles a dialog whose entries arrive after mount;
  // an empty initial list must not permanently turn a requested item into 0.
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const index = selectedIndex ?? clampIndex(initialIndex, total)
  const entry = entries[index]

  useEffect(() => {
    if (total === 0) {
      onClose()
      return
    }
    setSelectedIndex((current) => current === null ? clampIndex(initialIndex, total) : Math.min(current, total - 1))
  }, [initialIndex, onClose, total])

  const step = useCallback((delta: number) => {
    setSelectedIndex((current) => {
      if (total === 0) return 0
      const currentIndex = current ?? clampIndex(initialIndex, total)
      return (currentIndex + delta + total) % total
    })
  }, [initialIndex, total])

  useEffect(() => {
    const opener = document.activeElement
    dialogRef.current?.focus()
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (total < 2) return
      if (event.key === 'ArrowLeft') step(-1)
      if (event.key === 'ArrowRight') step(1)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, step, total])

  if (entry === undefined) return null

  const item = entry.origin === 'media' ? entry.item : null
  const merchandisingPosition = item === null || item.kind === 'instruction' || item.kind === 'unknown'
    ? null
    : entries.slice(0, index + 1).filter((candidate) => (
      candidate.origin === 'media' && (candidate.item.kind === 'image' || candidate.item.kind === 'video')
    )).length
  const primaryDisabled = item === null || item.kind !== 'image' || item.isPrimary
  const primaryDisabledText = item?.isPrimary ? ALREADY_PRIMARY_TEXT : MAKE_PRIMARY_PHOTO_ONLY_TEXT
  // The Russian sentence for an unavailable probe measurement is server-owned
  // and travels with the row. The stored/provider diagnostic code is never copy.
  const probeLine = item === null ? null : item.probeUnavailableMessage

  // Portalled to the document, not rendered where it was opened. The Warehouse
  // drawer is itself a portalled, animated box, and an animated ancestor becomes
  // the containing block for a fixed child, so a lightbox opened from inside the
  // drawer was clipped to the drawer instead of covering the screen.
  return createPortal(
    <div
      className="fixed inset-0 z-[120] grid place-items-center bg-onyx/90 p-4 backdrop-blur-[3px]"
      role="dialog"
      aria-modal="true"
      aria-label={heading ?? entryLabel(entry)}
      tabIndex={-1}
      ref={dialogRef}
      onMouseDown={onClose}
    >
      <div className="relative flex flex-col items-center gap-3" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex w-full flex-wrap items-center justify-between gap-3 text-ivory/90">
          <div className="min-w-0">
            {heading ? <p className="truncate text-[13px] font-medium">{heading}</p> : null}
            {item ? <p className="text-[12px] text-ivory/70">{positionCaption(item, merchandisingPosition)}</p> : null}
            {probeLine ? <p className="font-mono text-[12px] text-ivory/70">{probeLine}</p> : null}
          </div>
          <div className="flex items-center gap-2">
            {total > 1 ? <span className="text-[12px] tabular-nums text-ivory/70">{`${index + 1} / ${total}`}</span> : null}
            {onMakePrimary && item ? (
              <button
                type="button"
                disabled={primaryDisabled}
                title={primaryDisabled ? primaryDisabledText : undefined}
                onClick={() => { if (item) onMakePrimary(item) }}
                className="rounded-full border border-rule-strong px-3 py-1 text-[12px] text-ivory disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                {item.isPrimary ? ALREADY_PRIMARY_TEXT : MAKE_PRIMARY_TEXT}
              </button>
            ) : null}
            {onMakePrimary && item && primaryDisabled && !item.isPrimary ? (
              <span className="text-[12px] text-ivory/70">{primaryDisabledText}</span>
            ) : null}
            {onDelete && item ? (
              <button
                type="button"
                onClick={() => { if (item) onDelete(item) }}
                className="rounded-full border border-rule-strong px-3 py-1 text-[12px] text-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                {DELETE_TEXT}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              aria-label={CLOSE_TEXT}
              className="grid size-9 place-items-center rounded-full border border-rule-strong bg-paper text-xl leading-none text-ink shadow-md hover:bg-bone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {total > 1 ? (
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label={PREVIOUS_TEXT}
              className="grid size-12 shrink-0 place-items-center rounded-full border border-rule-strong bg-paper text-xl leading-none text-ink shadow-md hover:bg-bone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              ‹
            </button>
          ) : null}
          {item ? (
            <MediaStage key={item.id} item={item} />
          ) : (
            <div className={STAGE_CLASS}>
              <img src={entry.origin === 'catalog' ? entry.url : ''} alt={entryLabel(entry)} className={STAGE_MEDIA_CLASS} />
            </div>
          )}
          {total > 1 ? (
            <button
              type="button"
              onClick={() => step(1)}
              aria-label={NEXT_TEXT}
              className="grid size-12 shrink-0 place-items-center rounded-full border border-rule-strong bg-paper text-xl leading-none text-ink shadow-md hover:bg-bone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              ›
            </button>
          ) : null}
        </div>

        {total > 1 ? (
          <ul className="flex max-w-full flex-wrap justify-center gap-2">
            {entries.map((candidate, candidateIndex) => {
              if (candidate.origin !== 'media') return null
              const current = candidateIndex === index
              return (
                <li key={candidate.item.id} className="relative">
                  <button
                  type="button"
                  onClick={() => setSelectedIndex(candidateIndex)}
                    aria-current={current ? 'true' : undefined}
                    aria-label={mediaLabel(candidate.item)}
                    className={`grid size-[62px] overflow-hidden rounded-md border bg-onyx-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${current ? 'border-gold ring-2 ring-gold' : 'border-rule-strong'}`}
                  >
                    <FilmstripThumb item={candidate.item} />
                  </button>
                  {candidate.item.isPrimary ? (
                    <span aria-hidden="true" data-primary="true" className="pointer-events-none absolute -right-1 -top-1 text-[12px] leading-none text-gold">★</span>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : null}

        <p className="text-center text-[12px] text-ivory/90">{DISMISS_HINT_TEXT}</p>
      </div>
    </div>,
    document.body,
  )
}
